import clsx from "clsx";
import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  FC,
  MouseEvent,
  MouseEventHandler,
  ReactNode,
} from "react";
import { Link as RouterLink, LinkProps as RouterLinkProps } from "react-router";

import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";

type Size = "sm" | "lg";

type CommonProps = {
  // Optional so `<Link />` works as a `Trans` component tag (i18next injects
  // the translated segment as children at runtime).
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  inverted?: boolean;
  size?: Size;
};

type AnchorProps = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className"> & {
    href: string;
    to?: undefined;
  };

/** In-app navigation: renders a router link, so it is a real link (href). */
type RouteProps = CommonProps &
  Omit<RouterLinkProps, "className" | "children"> & {
    href?: undefined;
  };

type ButtonProps = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "type"> & {
    href?: undefined;
    to?: undefined;
    type?: "button" | "submit";
  };

type LinkProps = AnchorProps | RouteProps | ButtonProps;

const sizeClassName: Record<Size, string> = {
  lg: "!py-2 !text-body-base",
  sm: "!py-1.5 !text-body-sm",
};

const toneClassName = {
  default: {
    enabled: "!border-foreground-primary !text-foreground-primary",
    disabled:
      "!pointer-events-none !cursor-not-allowed !border-foreground-tertiary !text-foreground-tertiary",
  },
  inverted: {
    enabled:
      "!border-foreground-primary-inverse !text-foreground-primary-inverse",
    disabled:
      "!pointer-events-none !cursor-not-allowed !border-foreground-tertiary-inverse !text-foreground-tertiary-inverse",
  },
} as const;

export const Link: FC<LinkProps> = ({
  children,
  className,
  disabled = false,
  inverted = false,
  size = "lg",
  ...rest
}) => {
  const tone = toneClassName[inverted ? "inverted" : "default"];
  const sharedClassName = clsx(
    // Bang-prefixed: a global legacy stylesheet resets `a` / `button`
    // styles with plain (non-!important) declarations that would otherwise
    // win the cascade over same-specificity Tailwind utilities.
    // `group relative`: hosts FocusRing (outline utilities lose to form.scss).
    "group relative !inline-flex !items-center !justify-center !gap-1 !border-b !border-solid !font-semibold !no-underline outline-none",
    // Thicker underline on hover/focus: compensate the +1px border with -1px
    // margin so siblings (and the page footer) do not shift.
    "hover:!-mb-px hover:!border-b-2 focus-visible:!-mb-px focus-visible:!border-b-2",
    tone.enabled,
    disabled && tone.disabled,
    sizeClassName[size],
    className,
  );

  const content = (
    <>
      <FocusRing inverted={inverted} />
      {children}
    </>
  );

  // Both link kinds render an <a>: a disabled one stays focus- and click-inert.
  // Default tabIndex 0: Safari otherwise skips anchors in the Tab cycle.
  const anchorInteraction = (
    tabIndex?: number,
    onClick?: MouseEventHandler<HTMLAnchorElement>,
  ) => ({
    "aria-disabled": disabled,
    tabIndex: disabled ? -1 : (tabIndex ?? KEYBOARD_TAB_INDEX),
    onClick: disabled
      ? (event: MouseEvent<HTMLAnchorElement>) => event.preventDefault()
      : onClick,
  });

  if (rest.href) {
    const { href, tabIndex, onClick, ...anchorRest } = rest as AnchorProps;

    return (
      <a
        className={sharedClassName}
        href={disabled ? undefined : href}
        {...anchorInteraction(tabIndex, onClick)}
        {...anchorRest}
      >
        {content}
      </a>
    );
  }

  if (rest.to != null) {
    const { tabIndex, onClick, ...routeRest } = rest as RouteProps;

    return (
      <RouterLink
        className={sharedClassName}
        {...anchorInteraction(tabIndex, onClick)}
        {...routeRest}
      >
        {content}
      </RouterLink>
    );
  }

  const {
    type = "button",
    tabIndex = KEYBOARD_TAB_INDEX,
    ...buttonRest
  } = rest as ButtonProps;

  return (
    <button
      className={sharedClassName}
      type={type}
      tabIndex={disabled ? -1 : tabIndex}
      disabled={disabled}
      {...buttonRest}
    >
      {content}
    </button>
  );
};
